import { useState, useEffect, useCallback } from 'react';

import ADSearchField from './ADSearchField';
import DateField from './DateField';
import FileField from './FileField';
import FileUploadField from './FileUploadField';
import Header from './Header';
import SelectField from './SelectField';
import TextArea from './TextArea';
import TextField from './TextField';
import MultiSelectField, { MultiSelectOption } from './MultiSelectField';

import { errorToast } from '../components/Toasty';
import * as NCRDataConstants from '../constants/NCRDataConstants';

import { Stage } from '../enums/NCR';

import GetScreenFieldValue from '../functions/GetScreenFieldValue';
import TaskService from '../services/TaskService';
import Task from '../types/Task/Task';

type Props = {
    task: Task;
    files: File[];
    fileAddHandler: (e: any) => void;
    fileRemoveHandler: (e: any) => void;
    stageChange: (e: any) => void;
    onError?: (error: boolean) => void;
};

export default function QualityExpediting({
    task,
    files,
    fileAddHandler,
    fileRemoveHandler,
    stageChange,
    onError,
}: Props) {
    const getScreenFieldValue = useCallback(
        (dataName: string) => GetScreenFieldValue(task.screen.screenFields, dataName),
        [task.screen.screenFields]
    );

    const [closeNCRStep1Value, setCloseNCRStep1Value] = useState(
        () => getScreenFieldValue(NCRDataConstants.CLOSE_NCR_STEP_1)
    );
    const [closeNCRStep2Value, setCloseNCRStep2Value] = useState(
        () => getScreenFieldValue(NCRDataConstants.CLOSE_NCR_STEP_2)
    );
    const [targetImplementationDate, setTargetImplementationDate] = useState(
        () => getScreenFieldValue(NCRDataConstants.TARGET_IMPLEMENTATION_DATE)
    );
    const [actualImplementationDate, setActualImplementationDate] = useState(
        () => getScreenFieldValue(NCRDataConstants.ACTUAL_IMPLEMENTATION_DATE)
    );
    const [targetChanges, setTargetChanges] = useState(
        () => getScreenFieldValue(NCRDataConstants.IMPLEMENT_CHANGES)
    );
    const [assignActionItem, setAssignActionItem] = useState(
        () => getScreenFieldValue(NCRDataConstants.ASSIGN_TO_CHOICE)
    );
    const [assignImplementation, setAssignImplementation] = useState(
        () => getScreenFieldValue(NCRDataConstants.ASSIGN_TO_CHOICE_IMPLEMENTATION)
    );
    const [noActionReasons, setNoActionReasons] = useState<string[]>([]);
    const [closureStatuses, setClosureStatuses] = useState<string[]>([]);
    const [isNcrClosureStatusError, setIsNcrClosureStatusError] = useState(false);

    // Fetch data on mount
    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            try {
                const [reasonsResponse, statusesResponse] = await Promise.all([
                    TaskService.getDataSourceData(task.id.toString(), 'DATA_DEFINITIONS', '9'),
                    TaskService.getDataSourceData(task.id.toString(), 'DATA_DEFINITIONS', '10'),
                ]);
                if (cancelled) return;
                setNoActionReasons(reasonsResponse.data);
                setClosureStatuses(statusesResponse.data);
            } catch (e) {
                if (cancelled) return;
                console.log(e);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [task.id]);

    const checkIfQualityExpediting = () => task.stage === Stage.QualityExpediting;

    const checkIfCorporateAnalysis = () =>
        task.stage === Stage.QualityExpediting || task.stage === Stage.CorporateAnalysis;

    // Handler functions
    const handleCloseNCRStep1Change = (event: any) => {
        setCloseNCRStep1Value(event.target.value);
    };

    const handleCloseNCRStep2Change = (event: any) => {
        setCloseNCRStep2Value(event.target.value);
    };

    const handleTargetImplementationDateChange = (input: string) => {
        setTargetImplementationDate(input);
    };

    const handleActualImplementationDateChange = (input: string) => {
        setActualImplementationDate(input);
    };

    const handleTargetChangesChange = (event: any) => {
        setTargetChanges(event.target.value);
    };

    const handleAssignActionChange = (event: any) => {
        const newValue = event.target.value;
        setAssignActionItem(newValue);

        if (newValue === NCRDataConstants.ASSIGN_MYSELF) {
            stageChange(Stage.CorporateAnalysis);
        } else {
            stageChange(Stage.QualityExpediting);
        }
    };

    const handleAssignImplementationChange = (event: any) => {
        setAssignImplementation(event.target.value);
    };

    const onMultiSelectChange = (data: MultiSelectOption[]) => {
        const errorMessage = () => (
            <div className="text-center">
                <div><b>NCR Closure Status</b></div>
                <div>cannot have 'No Change'</div>
                <div>selected with other values. Please update your selection accordingly.</div>
            </div>
        );

        const selectedValues = data.map((x) => x.value);
        const errorPresent = selectedValues.some((x) => x === 'No Change') && selectedValues.length > 1;
        setIsNcrClosureStatusError(errorPresent);
        onError?.(errorPresent);
        if (errorPresent) {
            errorToast(errorMessage(), { autoClose: 3000, pauseOnHover: true, customID: '???' });
        }
    };

    // function to get media fields
    const getMediaFiles = () => {
        const fileNamesFields = task.screen.screenFields.filter((x) =>
            x.dataName.includes(NCRDataConstants.ACTION_ITEM_PHOTO_FILE_NAME)
        );
        const fileFields = task.screen.screenFields.filter((x) =>
            x.dataName.includes(NCRDataConstants.ACTION_ITEM_PHOTO_URL)
        );

        return fileNamesFields.map((field, i) => (
            <div key={i}>
                <TextField
                    dataName={field.dataName}
                    title={field.title}
                    value={field.value}
                    hintText={field.hintText}
                    readonly={true}
                />
                <FileField
                    title={fileFields[i].title}
                    value={fileFields[i].value}
                    hintText={fileFields[i].hintText}
                />
            </div>
        ));
    };

    // function to return quality expediting fields
    const qualityExpedtingFields = () => {
        const value = closeNCRStep1Value;
        if (value !== '') {
            if (value === NCRDataConstants.WITHOUT_ACTION_ITEMS) {
                return (
                    <div>
                        <SelectField
                            dataName={NCRDataConstants.REASON_NO_ACTION}
                            title={NCRDataConstants.REASON_NO_ACTION_TITLE}
                            value={getScreenFieldValue(NCRDataConstants.REASON_NO_ACTION)}
                            readonly={!checkIfQualityExpediting()}
                            required={checkIfQualityExpediting()}
                            options={noActionReasons.map((reason) => ({ value: reason }))}
                        />
                        <TextField
                            dataName={NCRDataConstants.NO_ACTION_COMMENTS}
                            title={NCRDataConstants.NO_ACTION_COMMENTS_TITLE}
                            value={getScreenFieldValue(NCRDataConstants.NO_ACTION_COMMENTS)}
                            readonly={!checkIfQualityExpediting()}
                            required={false}
                        />
                    </div>
                );
            } else {
                return (
                    <div>
                        {checkIfQualityExpediting() ? (
                            <SelectField
                                dataName={NCRDataConstants.ASSIGN_TO_CHOICE}
                                title={NCRDataConstants.ASSIGN_TO_CHOICE_TITLE}
                                value={assignActionItem}
                                options={[
                                    { value: NCRDataConstants.ASSIGN_MYSELF },
                                    { value: NCRDataConstants.ASSIGN_ANOTHER_PERSON },
                                ]}
                                readonly={!checkIfQualityExpediting()}
                                required={checkIfQualityExpediting()}
                                onchange={handleAssignActionChange}
                            />
                        ) : null}
                        {checkIfQualityExpediting() && assignActionItem === NCRDataConstants.ASSIGN_MYSELF
                            ? corporateAnalysis()
                            : null}
                        {checkIfQualityExpediting() && assignActionItem === NCRDataConstants.ASSIGN_ANOTHER_PERSON ? (
                            <ADSearchField
                                dataName={NCRDataConstants.ASSIGN_TO}
                                title={NCRDataConstants.ASSIGN_TO_TITLE}
                                disabled={!checkIfQualityExpediting()}
                                required={true}
                            />
                        ) : null}
                    </div>
                );
            }
        }
        return null;
    };

    // function to return corporate analysis fields
    const corporateAnalysis = () => {
        return (
            <div>
                <DateField
                    dataName={NCRDataConstants.TARGET_IMPLEMENTATION_DATE}
                    title={NCRDataConstants.TARGET_IMPLEMENTATION_DATE_TITLE}
                    value={targetImplementationDate}
                    readonly={!checkIfCorporateAnalysis()}
                    required={false}
                    defaultCurrentDate={true}
                    restrictPreviousDate={true}
                    onchange={handleTargetImplementationDateChange}
                />
                {checkIfCorporateAnalysis() ? (
                    <FileUploadField
                        dataName={NCRDataConstants.ACTION_ITEM_PHOTO_URL}
                        tableDataName={NCRDataConstants.ACTION_ITEM_PHOTO_TABLE}
                        filenameDataName={NCRDataConstants.ACTION_ITEM_PHOTO_FILE_NAME}
                        title={NCRDataConstants.PHOTO_TITLE}
                        multiple={true}
                        fileadd={fileAddHandler}
                        fileremove={fileRemoveHandler}
                        filesIn={files}
                    />
                ) : null}
                {getMediaFiles()}
                <SelectField
                    dataName={NCRDataConstants.CLOSE_NCR_STEP_2}
                    title={NCRDataConstants.CLOSE_NCR_STEP_2_TITLE}
                    options={[
                        { value: NCRDataConstants.WITH_ACTION_ITEMS },
                        { value: NCRDataConstants.WITHOUT_ACTION_ITEMS },
                    ]}
                    value={closeNCRStep2Value}
                    readonly={!checkIfCorporateAnalysis()}
                    required={true}
                    onchange={handleCloseNCRStep2Change}
                />
                {corporateActionItemFields()}
            </div>
        );
    };

    // function to return corporate action item fields
    const corporateActionItemFields = () => {
        const value = closeNCRStep2Value;
        if (value !== '') {
            if (value === NCRDataConstants.WITHOUT_ACTION_ITEMS) {
                return (
                    <div>
                        <SelectField
                            dataName={NCRDataConstants.REASON_NO_ACTION}
                            title={NCRDataConstants.REASON_NO_ACTION_TITLE}
                            value={getScreenFieldValue(NCRDataConstants.REASON_NO_ACTION)}
                            readonly={false}
                            required={true}
                            options={noActionReasons.map((reason) => ({ value: reason }))}
                        />
                        <TextField
                            dataName={NCRDataConstants.NO_ACTION_COMMENTS}
                            title={NCRDataConstants.NO_ACTION_COMMENTS_TITLE}
                            value={getScreenFieldValue(NCRDataConstants.NO_ACTION_COMMENTS)}
                            readonly={false}
                            required={false}
                        />
                    </div>
                );
            } else {
                return (
                    <div>
                        <TextField
                            dataName={NCRDataConstants.IMPLEMENT_CHANGES}
                            title={NCRDataConstants.IMPLEMENT_CHANGES_TITLE}
                            value={targetChanges}
                            readonly={!checkIfCorporateAnalysis()}
                            required={true}
                            onchange={handleTargetChangesChange}
                        />
                        {checkIfCorporateAnalysis() ? (
                            <SelectField
                                dataName={NCRDataConstants.ASSIGN_TO_CHOICE_IMPLEMENTATION}
                                title={NCRDataConstants.ASSIGN_TO_CHOICE_IMPLEMENTATION_TITLE}
                                value={assignImplementation}
                                options={[
                                    { value: NCRDataConstants.ASSIGN_MYSELF },
                                    { value: NCRDataConstants.ASSIGN_ANOTHER_PERSON },
                                ]}
                                readonly={false}
                                required={checkIfCorporateAnalysis()}
                                onchange={handleAssignImplementationChange}
                            />
                        ) : null}
                        {checkIfCorporateAnalysis() && assignImplementation === NCRDataConstants.ASSIGN_MYSELF
                            ? implementation()
                            : null}
                        {checkIfCorporateAnalysis() && assignImplementation === NCRDataConstants.ASSIGN_ANOTHER_PERSON ? (
                            <ADSearchField
                                dataName={NCRDataConstants.ASSIGN_TO_IMPLEMENTATION}
                                title={NCRDataConstants.ASSIGN_TO_TITLE}
                                disabled={!checkIfCorporateAnalysis()}
                                required={true}
                            />
                        ) : null}
                    </div>
                );
            }
        }
        return null;
    };

    // function to return implementation fields
    const implementation = () => {
        const actualChanges = getScreenFieldValue(NCRDataConstants.ACUTAL_IMPLEMENTATION_CHANGES);

        const MultiSelectOptions = closureStatuses.map((status: string) => ({
            label: status,
            value: status,
        }));
        const specialOptions = closureStatuses.slice(0, -1).map((status: string) => ({
            label: status,
            value: status,
        }));

        return (
            <div>
                <DateField
                    dataName={NCRDataConstants.ACTUAL_IMPLEMENTATION_DATE}
                    title={NCRDataConstants.ACTUAL_IMPLEMENTATION_DATE_TITLE}
                    value={actualImplementationDate === '' ? targetImplementationDate : actualImplementationDate}
                    readonly={false}
                    required={false}
                    onchange={handleActualImplementationDateChange}
                />
                <TextArea
                    dataName={NCRDataConstants.ACUTAL_IMPLEMENTATION_CHANGES}
                    title={NCRDataConstants.ACTUAL_IMPLEMENTATION_CHANGES_TITLE}
                    value={actualChanges === '' ? targetChanges : actualChanges}
                    readonly={false}
                    required={true}
                    rows={3}
                />
                <MultiSelectField
                    onChange={(data) => onMultiSelectChange(data)}
                    dataName={NCRDataConstants.CLOSURE_STATUS}
                    value={getScreenFieldValue(NCRDataConstants.CLOSURE_STATUS)}
                    options={MultiSelectOptions}
                    title={NCRDataConstants.CLOSURE_STATUS_TITLE}
                    required={true}
                    specialOptions={specialOptions}
                    specialOptionsTitle="Select All"
                    error={isNcrClosureStatusError}
                    errorMessage="Cannot have 'No Change' selected with other values."
                />
            </div>
        );
    };

    return (
        <div>
            <Header title={NCRDataConstants.CORPORATE_ANALYSIS} headerColor="white" textColor="black" />
            <SelectField
                dataName={NCRDataConstants.CLOSE_NCR_STEP_1}
                title={NCRDataConstants.CLOSE_NCR_STEP_1_TITLE}
                options={[
                    { value: NCRDataConstants.WITH_ACTION_ITEMS },
                    { value: NCRDataConstants.WITHOUT_ACTION_ITEMS },
                ]}
                value={closeNCRStep1Value}
                readonly={!checkIfQualityExpediting()}
                required={checkIfQualityExpediting()}
                onchange={handleCloseNCRStep1Change}
            />
            {qualityExpedtingFields()}
            {!checkIfQualityExpediting() ? corporateAnalysis() : null}
            {!checkIfCorporateAnalysis() ? implementation() : null}
        </div>
    );
}
